import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useProjectsForSelect } from '@/hooks/useProjectsQuery';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Stakeholder } from '@/pages/admin/StakeholderManagement';

interface StakeholderAssignDialogProps {
  stakeholder: Stakeholder | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function StakeholderAssignDialog({ 
  stakeholder, 
  isOpen, 
  onClose, 
  onSuccess 
}: StakeholderAssignDialogProps) {
  const [projectId, setProjectId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: projects = [] } = useProjectsForSelect();

  const handleAssign = async () => {
    if (!stakeholder || !projectId) return;
    
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('project_stakeholder_assignments')
        .insert({
          project_id: projectId,
          stakeholder_id: stakeholder.id,
          role: stakeholder.role || 'Stakeholder',
        });

      if (error) throw error;

      toast.success('Stakeholder assigned to project');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error('Failed to assign stakeholder', {
        description: error.message
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign to Project</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Assign <strong>{stakeholder?.name}</strong> to a project
          </p>
          
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger>
              <SelectValue placeholder="Select project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {project.project_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={!projectId || isSubmitting}>
              Assign
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
